import {Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, inject, OnInit, Signal, ViewChild} from '@angular/core';
import {JsonPipe} from '@angular/common';
import '@sapudex/web-components/dist/MastheadShell.js';
import '@sapudex/web-components/dist/MastheadItem.js';
import '@sapudex/web-components/dist/MastheadMenu.js';
import '@sapudex/web-components/dist/MastheadMobileMenu.js';
import '@sapudex/web-components/dist/MastheadProfileMenu.js';
import '@sapudex/web-components/dist/MastheadCountrySelector.js';
import {HeaderUserMenuService, LoggedInUser} from '@app/layout/header/services/user-menu/user-menu.service';
import {Country, RoutingService, SiteConnector} from '@spartacus/core';
import {Router} from '@angular/router';
import {DcpAuthService} from '@app/auth/services/dcp-auth.service';
import {FeatureTogglesService, SessionStorageService} from '@dcp/configurator';
import {CountrySelectorService} from '@app/services/country-selector.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {toSignal} from '@angular/core/rxjs-interop';
import {catchError, map} from 'rxjs/operators';
import {HeaderNavigationDataService} from '@app/layout/header/services/navigation-data/navigation-data.service';
import {mockExploreSapResponse, mockMainNavigationResponse} from '@app/layout/header/models/navigation-data.model';
import {of} from 'rxjs';
import {skipToContentAccessibility} from '@app/layout/header/utils/dcp-storefront-header-accessibility.util';
import {skipToContentConfig} from '@app/config/SkipToContentConfig';
import {UNLEASH_TOGGLES} from '@app/config/FeatureToggleConfig';

@UntilDestroy()
@Component({
  selector: 'dcp-header',
  templateUrl: './dcp-header.component.html',
  standalone: true,
  styleUrl: './dcp-header.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [JsonPipe],
})
export class DcpHeaderComponent implements OnInit {
  @ViewChild('udexAvatar', {static: false}) udexAvatarRef: ElementRef;
  public countrySelectorService = inject(CountrySelectorService);
  user: LoggedInUser;
  userMenuConfig = '';
  protected readonly authService = inject(DcpAuthService);
  isUserLoggedIn: Signal<boolean> = toSignal(this.authService.isUserLoggedIn(), {initialValue: false});
  protected headerUserMenuService = inject(HeaderUserMenuService);
  protected router = inject(Router);
  private readonly dcpRoutingService = inject(RoutingService);
  private readonly sessionStorageService = inject(SessionStorageService);
  private headerNavigationDataService = inject(HeaderNavigationDataService);
  public readonly featureTogglesService = inject(FeatureTogglesService);
  mainLinksNavigationData = toSignal(
    this.headerNavigationDataService.getMainNavigationData().pipe(
      map((data) =>
        this.headerNavigationDataService.transformNavigationData(data?.megaMenu || mockMainNavigationResponse.megaMenu)
      ),
      catchError(() => {
        return of(this.headerNavigationDataService.transformNavigationData(mockMainNavigationResponse.megaMenu));
      })
    ),
    {initialValue: this.headerNavigationDataService.transformNavigationData(mockMainNavigationResponse.megaMenu)}
  );
  exploreSapNavigationData = toSignal(
    this.headerNavigationDataService.getExploreSapNavigationData().pipe(
      map((config) => config?.exploreSapLink || mockExploreSapResponse.exploreSapLink),
      catchError(() => {
        return of(mockExploreSapResponse.exploreSapLink);
      })
    ),
    {initialValue: mockExploreSapResponse.exploreSapLink}
  );
  private siteConnector = inject(SiteConnector);
  countriesData = toSignal(
    this.siteConnector.getCountries().pipe(map((countries: Country[]) => this.transformCountries(countries))),
    {initialValue: null}
  );
  searchWebcomponentEnabled = toSignal(
    this.featureTogglesService.isEnabled(UNLEASH_TOGGLES.searchWebcomponentEnabled),
    {initialValue: false}
  );

  ngOnInit(): void {
    this.initializeNavigationData();
    this.initializeUserData();

    requestAnimationFrame(() => {
      this.addSkipToContentToLogo();
    });
  }

  addSkipToContentToLogo(): void {
    const udexShell = document.querySelector('udex-masthead-shell');
    if (!udexShell || !udexShell.shadowRoot) {
      return;
    }
    const logoElem = udexShell.shadowRoot.querySelector('[title="SAP"]');
    if (!logoElem) {
      return;
    }

    let wasMouseDown = false;

    // Track mouse clicks to prevent focus handler from interfering
    logoElem.addEventListener('mousedown', () => {
      wasMouseDown = true;
    });

    logoElem.addEventListener('mouseup', () => {
      wasMouseDown = false;
    });

    logoElem.addEventListener('focus', (event: Event) => {
      // Only run skip-to-content logic if not clicked (i.e., tabbed to)
      if (!wasMouseDown) {
        this.skipToContent(event as FocusEvent);
      }
      wasMouseDown = false;
    });
  }

  skipToContent(event: FocusEvent) {
    if (this.isSkipToContentActiveForRoute()) {
      skipToContentAccessibility(event);
    }
  }

  isSkipToContentActiveForRoute(): boolean {
    let isActive = false;

    this.dcpRoutingService
      .getRouterState()
      .pipe(untilDestroyed(this))
      .subscribe((routerState) => {
        isActive = skipToContentConfig.accessibility?.skipToContent?.paths?.includes(routerState.state?.semanticRoute);
      });
    return isActive;
  }

  onCountrySelected(isoCode: string) {
    if (isoCode) {
      this.countrySelectorService.initializeCountrySwitchWarning(isoCode);
    }
  }

  onLogin() {
    this.sessionStorageService.setItem('loginMethod', 'manual');
    this.authService.login();
  }

  submitSearch(event: CustomEvent): void {
    interface MastheadShellElement extends HTMLElement {
      closeSearch(): void;

      closeMobileMenu(): void;
    }

    let searchParamKey = 'query';
    // change 'query' to 'search' if search webcomponent is enabled
    if (this.searchWebcomponentEnabled()) {
      searchParamKey = 'search';
    }

    this.dcpRoutingService
      .go(
        {
          cxRoute: 'search',
        },
        {
          queryParams: {
            [searchParamKey]: event.detail.value,
          },
        }
      )
      .finally(() => {
        const mastheadShell = document.getElementById('masthead-shell') as MastheadShellElement;
        if (mastheadShell) {
          mastheadShell.closeSearch();
          mastheadShell.closeMobileMenu();
        }
      });
  }

  private initializeNavigationData(): void {
    this.headerUserMenuService
      .getUserMenuConfig()
      .pipe(untilDestroyed(this))
      .subscribe((userMenu) => {
        this.userMenuConfig = JSON.stringify(userMenu);
      });
  }

  private initializeUserData(): void {
    this.headerUserMenuService
      .getUserData()
      .pipe(untilDestroyed(this))
      .subscribe((user) => {
        this.user = user;
      });
  }

  private transformCountries(countries: Country[]) {
    return [
      {
        title: 'World',
        countries: countries.map((country) => ({
          displayTitle: country.name.replace(/'/g, '&#39;'),
          countryCode: country.isocode,
          url: 'javascript:void(0)', // NOSONAR
        })),
      },
    ];
  }
}
